import React, { useEffect, useState } from 'react';
import { Mail, Flame, Sun, Snowflake, Send, ChevronDown, ChevronUp, CheckCircle2 } from 'lucide-react';
import { apiClient } from '../../api/client';

const QUESTIONS = [
  ['private_investor', 'Private investor?'],
  ['investment_club', 'Investment club / syndicate?'],
  ['similar_experience', 'Invested in similar business?'],
  ['just_browsing', 'Just browsing?'],
];

const GRADES = [
  ['hot', 'Hot', Flame, 'bg-rose-100 text-rose-700 border-rose-300'],
  ['warm', 'Warm', Sun, 'bg-amber-100 text-amber-700 border-amber-300'],
  ['cold', 'Cold', Snowflake, 'bg-slate-100 text-slate-600 border-slate-300'],
];

const YesNo = ({ value, onChange }) => (
  <div className="flex gap-1">
    {['yes', 'no'].map(v => (
      <button key={v} onClick={() => onChange(value === v ? null : v)}
        className={`px-2 py-0.5 rounded text-[10px] font-black uppercase border transition-colors ${
          value === v
            ? v === 'yes' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' : 'bg-gray-200 text-gray-700 border-gray-300'
            : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300'
        }`}>{v}</button>
    ))}
  </div>
);

const LeadCard = ({ lead, onSaved }) => {
  const [open, setOpen] = useState(false);
  const q = lead.qualification || {};
  const [answers, setAnswers] = useState({
    private_investor: q.private_investor || null,
    investment_club: q.investment_club || null,
    similar_experience: q.similar_experience || null,
    just_browsing: q.just_browsing || null,
  });
  const [reply, setReply] = useState(q.reply_text || '');
  const [grade, setGrade] = useState(q.grade || null);
  const [saving, setSaving] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [invite, setInvite] = useState(null);
  const [sendingQual, setSendingQual] = useState(false);
  const [qualError, setQualError] = useState(null);

  const sendQualification = async () => {
    setSendingQual(true);
    setQualError(null);
    try {
      await apiClient.request(`/api/vault/qualification/${lead.investor_id}/send-qualification`, { method: 'POST' });
      onSaved();
    } catch (e) {
      setQualError(e?.message || 'Send failed');
    }
    setSendingQual(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.request(`/api/vault/qualification/${lead.investor_id}/grade`, {
        method: 'POST',
        body: JSON.stringify({ ...answers, reply_text: reply, grade }),
      });
      onSaved();
    } catch {}
    setSaving(false);
  };

  const inviteRoom = async () => {
    setInviting(true);
    try {
      const r = await apiClient.request(`/api/vault/qualification/${lead.investor_id}/invite-room`, {
        method: 'POST', body: JSON.stringify({ expires_hours: 168 }),
      });
      setInvite(r);
      onSaved();
    } catch {}
    setInviting(false);
  };

  const gradeMeta = GRADES.find(g => g[0] === (lead.qualification?.grade || grade));
  const room = invite || lead.room;

  return (
    <div className="border border-gray-100 rounded-xl bg-white" data-testid={`qual-lead-${lead.investor_id}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left" data-testid={`qual-lead-toggle-${lead.investor_id}`}>
        <div className="min-w-0 flex items-center gap-3">
          <div className="min-w-0">
            <p className="text-xs font-black text-gray-900 truncate">{lead.name} <span className="font-medium text-gray-400">· {lead.email}</span></p>
            <p className="text-[10px] text-gray-500 mt-0.5">
              {lead.source === 'pdf_request' ? 'One-Pager download' : lead.source === 'public_gateway' ? 'Interest form' : lead.source || 'Lead'}
              {lead.firm ? ` · ${lead.firm}` : ''}
              {lead.qualification_email_sent_at ? ` · Qualification sent ${new Date(lead.qualification_email_sent_at).toLocaleDateString('en-GB')}` : ' · Qualification not sent yet'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {room?.passcode && <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-[9px] font-black uppercase">Room · {room.passcode}</span>}
          {gradeMeta && <span className={`px-2 py-0.5 rounded-full border text-[9px] font-black uppercase ${gradeMeta[3]}`}>{gradeMeta[1]}</span>}
          {open ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-50 pt-3">
          {!lead.qualification_email_sent_at && (
            <div className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <p className="text-[11px] font-semibold text-amber-800">This lead never received the four-question qualification email.</p>
              <button onClick={sendQualification} disabled={sendingQual}
                className="flex-shrink-0 px-3 py-1.5 rounded-full bg-amber-600 text-white text-[11px] font-black hover:bg-amber-700 disabled:opacity-50 transition-colors"
                data-testid={`qual-send-email-${lead.investor_id}`}>
                {sendingQual ? 'Sending…' : 'Send Qualification Email'}
              </button>
            </div>
          )}
          {qualError && <p className="text-[11px] text-rose-600 font-semibold" data-testid={`qual-send-error-${lead.investor_id}`}>{qualError}</p>}
          <div className="grid sm:grid-cols-2 gap-2">
            {QUESTIONS.map(([key, label]) => (
              <div key={key} className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg px-3 py-2">
                <span className="text-[11px] font-semibold text-gray-700">{label}</span>
                <YesNo value={answers[key]} onChange={v => setAnswers(a => ({ ...a, [key]: v }))} />
              </div>
            ))}
          </div>
          <textarea value={reply} onChange={e => setReply(e.target.value)} rows={3}
            placeholder="Paste the lead's email reply here…"
            className="w-full text-xs border border-gray-200 rounded-lg p-3 focus:outline-none focus:border-[#7B4FD8]"
            data-testid={`qual-reply-${lead.investor_id}`} />
          <div className="flex flex-wrap items-center gap-2">
            {GRADES.map(([key, label, Icon, cls]) => (
              <button key={key} onClick={() => setGrade(grade === key ? null : key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[11px] font-black transition-colors ${grade === key ? cls : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300'}`}
                data-testid={`qual-grade-${key}-${lead.investor_id}`}>
                <Icon size={12} /> {label}
              </button>
            ))}
            <div className="flex-1" />
            <button onClick={save} disabled={saving}
              className="px-4 py-1.5 rounded-full bg-[#1A1209] text-white text-[11px] font-black hover:bg-black transition-colors disabled:opacity-50"
              data-testid={`qual-save-${lead.investor_id}`}>
              {saving ? 'Saving…' : 'Save Grade'}
            </button>
            {room?.passcode ? (
              <span className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-600" data-testid={`qual-invited-${lead.investor_id}`}>
                <CheckCircle2 size={13} /> Room live · code {room.passcode}{invite?.email_sent ? ' · emailed ✓' : ''}
              </span>
            ) : (
              <button onClick={inviteRoom} disabled={inviting}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#7B4FD8] text-white text-[11px] font-black hover:bg-[#5B32C8] transition-colors disabled:opacity-50"
                data-testid={`qual-invite-${lead.investor_id}`}>
                <Send size={12} /> {inviting ? 'Creating…' : 'Invite to Data Room (7 days)'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const QualificationInbox = () => {
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState(null);
  const fetchLeads = async () => {
    try {
      const d = await apiClient.request('/api/vault/qualification/leads');
      setLeads(d.leads || []);
      setError(null);
    } catch (e) {
      setError(e?.message || 'Could not load leads');
    }
  };
  useEffect(() => { fetchLeads(); }, []);

  if (error) return (
    <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 text-xs text-rose-700 font-semibold" data-testid="qualification-inbox-error">
      Qualification Inbox couldn't load leads: {error}
    </div>
  );
  if (!leads.length) return null;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm" data-testid="qualification-inbox">
      <h3 className="text-sm font-black text-[#1A1209] uppercase tracking-wider mb-1 flex items-center gap-2">
        <Mail size={14} className="text-[#7B4FD8]" /> Qualification Inbox ({leads.length})
      </h3>
      <p className="text-[11px] text-gray-500 mb-4">Every lead from the One-Pager gate and interest form. Record their reply to your four questions, grade them, and invite the good ones to a timed Data Room in one click.</p>
      <div className="space-y-2">
        {leads.map(lead => <LeadCard key={lead.investor_id} lead={lead} onSaved={fetchLeads} />)}
      </div>
    </div>
  );
};

export default QualificationInbox;
